// NPM
import { isNil, omit, omitBy, isEmpty } from "lodash";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

// HOOKS
import useQuery from "Hooks/query.hook";
import useSession from "Hooks/session.hook";
import usePagination from "Hooks/pagination.hook";

// SERVICES
import {
  fetchBoxes,
  fetchRacks,
  fetchTrolleys,
  listVehicles,
  fetchVehicle,
  updateBox,
  updateRack,
  updateSlot,
  updateTrolley,
  updateVehicle,
  createAlgoTiming,
  listAlgoTiming,
  listVehicleTypes,
} from "Services/delivery-settings.services";

// HELPERS
import { decode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";

// STYLE
import "Components/delivery-management/delivery-management.scss";

// COMPONENTS
import { Tabs } from "@life-line-lab/components-react";
import {
  BoxList,
  RackList,
  TrolleyList,
  VehicleList,
  RoutingList,
} from "Components/delivery-management/delivery-lists";
import ModuleTitleBar from "Components/shared/module-title-bar";
import { RackView } from "Components/delivery-management/delivery-views";
import VehicleTypesList from "Components/delivery-management/delivery-lists/vehicle-type-list";

const initialModal = {
  open: false,
  slotData: null,
};
const initialFilters: any = {};

const useDeliveryAssets = (filters: any) => {
  const { org } = useSession();
  const { t } = useTranslation();

  const [response, setResponse] = useState<any>([]);
  const [currentListCount, setCurrentListCount] = useState(0);
  const [routingData, setRoutingData] = useState<any>([]);
  const [modal, setModal] = useState<any>(initialModal);

  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
    resetPageDeps: filters
  });

  const handleFetchRacks = async (filters?: any) => {
    const responseData = await fetchRacks(omitBy(filters, isNil), queryParams.offset, itemPerPage);
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleFetchBoxes = async (filters?: any) => {
    const responseData = await fetchBoxes(omitBy(filters, isNil), queryParams.offset, itemPerPage);
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleFetchTrolleys = async (filters?: any) => {
    const responseData = await fetchTrolleys(
      omitBy(filters, isNil),
      queryParams.offset,
      itemPerPage,
    );
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleFetchVehicles = async (filters?: any) => {
    const responseData = await listVehicles(
      omitBy(filters, isNil),
      queryParams.offset,
      itemPerPage,
    );
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleFetchVehicleTypes = async (filters?: any) => {
    const responseData = await listVehicleTypes(
      omitBy(filters, isNil),
      queryParams.offset,
      itemPerPage,
    );
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleUpdateRackStatus = async (value: string, data: any) => {
    data.orgID = org._id;
    data.active = value == "true";
    let responseData: any = await updateRack(omit(data, ["_rev", "slots"]));
    if (!isNil(responseData) && responseData.success) {
      handleFetchRacks();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleUpdateSlotStatus = async (value: string, data: any, rackID: string) => {
    data.rackID = rackID;
    data.active = value == "true";
    let responseData: any = await updateSlot(omit(data, ["_rev"]));
    if (!isNil(responseData) && responseData.success) {
      handleFetchRacks();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleUpdateBoxStatus = async (value: string, data: any) => {
    data.orgID = org._id;
    data.active = value == "true";
    let responseData: any = await updateBox(omit(data, ["_rev"]));
    if (!isNil(responseData) && responseData.success) {
      handleFetchBoxes();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleUpdateTrolleyStatus = async (value: string, data: any) => {
    data.orgID = org._id;
    data.active = value == "true";
    let responseData: any = await updateTrolley(omit(data, ["_rev"]));
    if (!isNil(responseData) && responseData.success) {
      handleFetchTrolleys();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleUpdateVehicleStatus = async (value: string, data: any) => {
    data.orgID = org._id;
    data.active = value == "true";
    data.driverID = data.driver[0].profileID;
    let responseData: any = await updateVehicle(omit(data, ["_rev", "driver"]));
    if (!isNil(responseData) && responseData.success) {
      handleFetchVehicles();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleUpdateSlot = async (data: any) => {
    let responseData: any = await updateSlot(data);
    if (!isNil(responseData) && responseData.success) {
      handleFetchRacks();
      setModal(initialModal);
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleCreateRoutingTime = async (inputData: any) => {
    const responseData: any = await createAlgoTiming(inputData);

    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateRoutingTime = async (value, inputData: any, type) => {
    // console.log('we got here with value,', value,'inputData',inputData, 'type',type)
  };

  const handleListingRoutingTime = async () => {
    const responseData = await listAlgoTiming();

    if (!isNil(responseData)) {
      setRoutingData(responseData);
    } else {
      console.log("error", responseData);
    }
  };

  return {
    modal,
    setModal,
    paginationComponent,
    itemPerPage,
    queryParams,
    setQueryParams,
    response,
    setResponse,
    currentListCount,
    setCurrentListCount,
    routingData,
    setRoutingData,
    handleFetchRacks,
    handleFetchBoxes,
    handleFetchTrolleys,
    handleFetchVehicles,
    handleFetchVehicleTypes,
    handleUpdateRackStatus,
    handleUpdateSlotStatus,
    handleUpdateBoxStatus,
    handleUpdateTrolleyStatus,
    handleUpdateVehicleStatus,
    handleUpdateSlot,
    handleCreateRoutingTime,
    handleUpdateRoutingTime,
    handleListingRoutingTime,
  };
};

const DeliveryAssets: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();

  const id = query.get("id");
  const tabKey = query.get("tabKey") || "0";
  const [filters, setFilters] = useState<any>(initialFilters);

  const {
    modal,
    setModal,
    paginationComponent,
    itemPerPage,
    queryParams,
    setQueryParams,
    response,
    setResponse,
    currentListCount,
    setCurrentListCount,
    routingData,
    setRoutingData,
    handleFetchRacks,
    handleFetchBoxes,
    handleFetchTrolleys,
    handleFetchVehicles,
    handleFetchVehicleTypes,
    handleUpdateRackStatus,
    handleUpdateSlotStatus,
    handleUpdateBoxStatus,
    handleUpdateTrolleyStatus,
    handleUpdateVehicleStatus,
    handleUpdateSlot,
    handleCreateRoutingTime,
    handleUpdateRoutingTime,
    handleListingRoutingTime,
  } = useDeliveryAssets(filters);

  const [activeTabKey, setActiveTabKey] = useState(tabKey);

  const handleApiCall = () => {
    switch (tabKey) {
      case "0":
        handleFetchRacks(filters);
        break;
      case "1":
        handleFetchBoxes(filters);
        break;
      case "2":
        handleFetchTrolleys(filters);
        break;
      case "3":
        handleFetchVehicleTypes(filters);
        break;
      case "4":
        handleFetchVehicles(filters);
        break;
      case "5":
        handleListingRoutingTime();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    tabKey === "0"
      ? history.push(`/delivery-management/settings/assets?page=${queryParams.page}`)
      : history.push(
          `/delivery-management/settings/assets?tabKey=${tabKey}&page=${queryParams.page}`,
        );
  }, [queryParams.page]);

  // separate useEffects to prevent api being called twice
  useEffect(() => {
    handleApiCall();

    return () => {
      setResponse([]);
    };
  }, [tabKey, queryParams.offset, itemPerPage]);

  useEffect(() => {
    // if filter is empty, do not call the api
    const hasFilter = omitBy(filters, isNil);

    if (!isEmpty(hasFilter)) handleApiCall();
  }, [filters]);

  const handleFilters = (filterInput) => {
    setFilters({
      type: filterInput.byType || null,
      line: filterInput.byLine || null,
      orFields: filterInput.orFields || null,
    });
  };

  // tabKey is used to control the url to forms, as the component does not use this
  const tabs = [
    {
      tabKey: "0",
      label: t("title.racks"),
      component: (
        <RackList
          offset={queryParams.offset}
          list={response}
          onFilter={handleFilters}
          onUpdateStatus={handleUpdateRackStatus}
        />
      ),
    },
    {
      tabKey: "1",
      label: t("title.boxes"),
      component: (
        <BoxList
          offset={queryParams.offset}
          list={response}
          onFilter={handleFilters}
          onUpdateStatus={handleUpdateBoxStatus}
        />
      ),
    },
    {
      tabKey: "2",
      label: t("title.trolleys"),
      component: (
        <TrolleyList
          offset={queryParams.offset}
          list={response}
          onFilter={handleFilters}
          onUpdateStatus={handleUpdateTrolleyStatus}
        />
      ),
    },
    {
      tabKey: "3",
      label: t("title.vehicleTypes"),
      component: (
        <VehicleTypesList
          tabKey={"3"}
          offset={queryParams.offset}
          list={response}
          onFilter={handleFilters}
          onUpdateStatus={handleUpdateVehicleStatus}
        />
      ),
    },
    {
      tabKey: "4",
      label: t("title.vehicles"),
      component: (
        <VehicleList
          tabKey={"4"}
          offset={queryParams.offset}
          list={response}
          onFilter={handleFilters}
          onUpdateStatus={handleUpdateVehicleStatus}
        />
      ),
    },
    {
      tabKey: "5",
      label: t("title.routingAlgorithm"),
      component: (
        <RoutingList
          onCreateTime={handleCreateRoutingTime}
          routingData={routingData}
          onUpdateTime={handleUpdateRoutingTime}
        />
      ),
    },
  ];

  // to change url path when the tab change
  const handleTabClick = (key: string) => {
    setActiveTabKey(key);
    setQueryParams({ page: 1, offset: 0 });
    key === "0"
      ? history.push(`/delivery-management/settings/assets?page=1`)
      : history.push(`/delivery-management/settings/assets?tabKey=${key}&page=1`);
  };

  return (
    <div className="delivery-management assets">
      <Helmet>
        <title>{t("pageTitle.deliverySetup")}</title>
      </Helmet>

      <div className={id ? "" : "list-title"}>
        <ModuleTitleBar titleText={t("title.deliverySetup")} />
      </div>

      {id ? (
        <RackView
          rackData={response.find((rackObj) => rackObj.rack._id == decode(id))}
          onUpdate={handleUpdateSlot}
          onUpdateStatus={handleUpdateSlotStatus}
          modal={modal}
          setModal={setModal}
        />
      ) : (
        <>
          <Tabs
            destroyInactiveTabPane={true}
            onTabClick={handleTabClick}
            defaultActiveKey="0"
            activeKey={activeTabKey}
            tabBarGutter={25}
            tabs={tabs}
          />
          <div className="my-30">{paginationComponent}</div>
        </>
      )}
    </div>
  );
};

export default DeliveryAssets;
